/**
 * What the onboarding knows about you, and how it found out.
 *
 * THE RULE: a step is ticked by evidence, never by the reader promising. A
 * checklist that ticks itself when you press "done" teaches nothing and lies
 * about the workspace two days later. So each step in `content.ts` declares one
 * of two kinds of evidence, and this module supplies both:
 *
 *   `visit`  You went to that screen. `useOnboardingTracking` runs in the app
 *            shell, watches the location, and writes the step id down. This is
 *            a fact about the browser, so it lives in local storage.
 *
 *   `data`   The workspace can prove it — you own an inquiry, a task is yours,
 *            a saved view has your name on it. Read live from the server on
 *            every render, so it is never stale and it survives a new laptop.
 *
 * A `data` step can be true the first time you ever sign in, because a
 * workspace that has been running a year already has your name on things. The
 * card says so ("already true — you own 12") instead of pretending you just did
 * it; see `Checklist.tsx`. Ticking something that is genuinely true is not a
 * lie, and the teaching is in the card body either way.
 *
 * STORAGE IS PER USER. The key carries the user id, so two people sharing a
 * machine do not inherit each other's ticks — and signing out and back in as
 * somebody else does not hand them a finished checklist they never worked
 * through.
 */

import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { STEPS, type StepId } from '@/features/onboarding/content';
import { useInquiries, useSavedViews, useSession, useTasks } from '@/lib/queries';
import { useLocalStorage } from '@/lib/useLocalStorage';

/* ────────────────────────────────────────────────────────────────────────────
   Storage
   ──────────────────────────────────────────────────────────────────────────── */

export interface OnboardingState {
  /** Step ids whose evidence is "you have been there". */
  visited: StepId[];
  /** The guided tour has been run to the end at least once. */
  tourSeen: boolean;
  /**
   * The reader has said, on the Getting started screen, that they are done with
   * the rail's nudge. It hides the badge; it does not hide the screen, which
   * stays reachable from the rail, the account menu and the command palette
   * forever. Onboarding you cannot get back to is a manual you threw away.
   */
  dismissed: boolean;
}

const EMPTY: OnboardingState = { visited: [], tourSeen: false, dismissed: false };

/** `af.onboarding.<user id>` — see the per-user note in the file header. */
export function onboardingStorageKey(userId: string | undefined): string {
  return `af.onboarding.${userId ?? 'anonymous'}`;
}

/**
 * The stored half of the state. Cross-tab synced and SSR-safe, like every other
 * preference in the product — `useLocalStorage` does that work.
 */
export function useOnboardingState(): [OnboardingState, (next: OnboardingState) => void] {
  const { data: session } = useSession();
  const [state, setState] = useLocalStorage<OnboardingState>(
    onboardingStorageKey(session?.user.id),
    EMPTY,
  );
  /* A key written by an older build, or hand-edited, must not crash the shell:
     every field is defaulted rather than trusted. */
  const safe = useMemo<OnboardingState>(
    () => ({
      visited: Array.isArray(state?.visited) ? state.visited : [],
      tourSeen: state?.tourSeen === true,
      dismissed: state?.dismissed === true,
    }),
    [state],
  );
  return [safe, setState];
}

/* ────────────────────────────────────────────────────────────────────────────
   Evidence 1 — where you have been
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Which `visit` step a path is evidence for. A record is evidence for BOTH the
 * list and the record, because you cannot reach `/inbox/AF-2418` without the
 * Inbox — ticking only the record would leave a checklist that reads as though
 * you skipped a step you demonstrably did not.
 */
function stepsForPath(pathname: string): StepId[] {
  if (/^\/inbox\/[^/]+/.test(pathname)) return ['inbox', 'record'];
  if (pathname === '/inbox' || pathname.startsWith('/inbox')) return ['inbox'];
  if (pathname.startsWith('/board')) return ['board'];
  return [];
}

/**
 * Mounted ONCE, by the app shell. Not by the Getting started screen: the whole
 * point is that the checklist fills in while you use the product, including on
 * the days you never open the checklist.
 */
export function useOnboardingTracking(): void {
  const { pathname } = useLocation();
  const [state, setState] = useOnboardingState();

  useEffect(() => {
    const earned = stepsForPath(pathname);
    if (earned.length === 0) return;
    const fresh = earned.filter((id) => !state.visited.includes(id));
    if (fresh.length === 0) return;
    setState({ ...state, visited: [...state.visited, ...fresh] });
    // `state` is deliberately out of the dependency list: it changes identity on
    // every write, and re-running on our own write would be a loop. The path is
    // the trigger; the guard above makes a stale read harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}

/* ────────────────────────────────────────────────────────────────────────────
   Evidence 2 — what the workspace can prove

   Three `limit: 1` list queries. The server counts and returns `total`, so one
   row on the wire answers "does this reader own anything at all" — the same
   shape the sidebar's two badges use, and for the same reason.
   ──────────────────────────────────────────────────────────────────────────── */

function useOwnedCount(userId: string | undefined): number | undefined {
  const { data } = useInquiries(
    {
      limit: 1,
      filters: {
        combinator: 'and',
        conditions: [{ id: 'owner', field: 'ownerId', operator: 'is', value: userId ?? '' }],
      },
    },
    { enabled: Boolean(userId) },
  );
  return data?.total;
}

function useMyTaskCount(userId: string | undefined): number | undefined {
  const { data } = useTasks(
    {
      limit: 1,
      filters: {
        combinator: 'and',
        conditions: [{ id: 'assignee', field: 'assigneeId', operator: 'is', value: userId ?? '' }],
      },
    },
    { enabled: Boolean(userId) },
  );
  return data?.total;
}

/* ────────────────────────────────────────────────────────────────────────────
   The progress model
   ──────────────────────────────────────────────────────────────────────────── */

export interface StepProgress {
  id: StepId;
  done: boolean;
  /**
   * The count behind a `data` step, or `undefined` while it is loading or for a
   * `visit` step. The card prints it, which is what stops "already ticked" from
   * reading as a broken checklist: `you own 12` is a different claim from
   * `you did this just now`, and only the first one is true on day one.
   */
  count?: number;
}

export interface OnboardingProgress {
  steps: StepProgress[];
  byId: Record<StepId, StepProgress>;
  done: number;
  total: number;
  /** Every step has its evidence. The rail stops nudging. */
  complete: boolean;
  /** Nothing has been read or done yet — the screen greets rather than resumes. */
  untouched: boolean;
  tourSeen: boolean;
  dismissed: boolean;
  /** Should the rail draw a badge? Complete or dismissed both mean no. */
  nudge: boolean;
  loading: boolean;
}

export function useOnboardingProgress(): OnboardingProgress {
  const { data: session } = useSession();
  const [state] = useOnboardingState();
  const userId = session?.user.id;

  const owned = useOwnedCount(userId);
  const myTasks = useMyTaskCount(userId);
  const views = useSavedViews();
  const myViews = views.data?.filter((view) => view.ownerId === userId).length;

  return useMemo(() => {
    const counts: Partial<Record<StepId, number | undefined>> = {
      owner: owned,
      task: myTasks,
      view: myViews,
    };

    const steps: StepProgress[] = STEPS.map((meta) => {
      if (meta.kind === 'visit') {
        return { id: meta.id, done: state.visited.includes(meta.id) };
      }
      const count = counts[meta.id];
      return { id: meta.id, done: (count ?? 0) > 0, count };
    });

    const byId = Object.fromEntries(steps.map((step) => [step.id, step])) as Record<
      StepId,
      StepProgress
    >;
    const done = steps.filter((step) => step.done).length;
    const complete = done === steps.length;

    return {
      steps,
      byId,
      done,
      total: steps.length,
      complete,
      untouched: done === 0 && !state.tourSeen,
      tourSeen: state.tourSeen,
      dismissed: state.dismissed,
      nudge: !complete && !state.dismissed,
      loading: owned === undefined || myTasks === undefined || views.isPending,
    };
  }, [owned, myTasks, myViews, views.isPending, state]);
}
